import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Assignment, Close, Link as LinkIcon, Paid } from "@mui/icons-material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Collaborator, CollaboratorPaymentResult, CollaboratorProntuario, deleteCollaboratorById, sendCollaboratorSSOLink, softDeleteCollaborator } from "src/apis/company";
import NoDataAlert from "src/pages/components/NoDataAlert";
import { Id } from "src/utils/types";
import IsBlockedCheckboxCell from "./IsBlockedCheckboxCell";
import DeleteCollaboratorDialog from "./DeleteCollaboratorDialog";
import { isUserAdministrator } from "src/utils/auth";
import CompanyBlockCheckboxCell from "./CompanyBlockCheckboxCell";
import CollaboratorPaymentDialog from "./CollaboratorPaymentDialog";
import TableActions from "src/pages/components/TableActions";
import EezycoinWalletDialog from "src/pages/components/eezycoin/EezycoinWalletDialog";

type CollaboratorsTableProps = {
  collaborators: Collaborator[];
  companyId: Id;
  reloadList: () => Promise<void>;
  defaultTargetCompanyId?: number;
};

type ProntuarioModalProps = {
  open: boolean;
  collaboratorName: string;
  prontuario: CollaboratorProntuario | null | undefined;
  onClose: () => void;
};

const ProntuarioModal: FC<ProntuarioModalProps> = ({
  open,
  collaboratorName,
  prontuario,
  onClose,
}) => {
  const hasMedicamentos = Boolean(prontuario?.medicamentos_em_uso);
  const hasContatos =
    Array.isArray(prontuario?.contatos_emergencia) &&
    (prontuario?.contatos_emergencia?.length ?? 0) > 0;
  const hasAnyData = hasMedicamentos || hasContatos;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Assignment fontSize="small" />
          Prontuário — {collaboratorName}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!hasAnyData ? (
          <Typography color="text.secondary" textAlign="center" py={2}>
            Nenhuma informação de prontuário cadastrada.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {hasMedicamentos && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Medicamentos em uso
                </Typography>
                <Typography variant="body2">{prontuario?.medicamentos_em_uso}</Typography>
              </Box>
            )}

            {hasMedicamentos && hasContatos && <Divider />}

            {hasContatos && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Contatos de emergência
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {prontuario?.contatos_emergencia?.map((contato, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        padding: 1.5,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{contato.nome}</strong>
                        {contato.parentesco && (
                          <Chip
                            label={contato.parentesco}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contato.telefone}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CollaboratorsTable: FC<CollaboratorsTableProps> = ({
  collaborators,
  companyId,
  reloadList,
  defaultTargetCompanyId,
}): JSX.Element => {
  const hasCollaborators = useMemo(() => Boolean(collaborators) && collaborators.length > 0, [collaborators]);
  const [entries, setEntries] = useState(collaborators);

  const isAdmin = useMemo<boolean>(isUserAdministrator, []);

  const FORCED_TARGET_ID = Number(defaultTargetCompanyId ?? 45);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Collaborator | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [prontuarioOpen, setProntuarioOpen] = useState(false);
  const [prontuarioCollaborator, setProntuarioCollaborator] = useState<Collaborator | null>(null);

  const [paymentTarget, setPaymentTarget] = useState<Collaborator | null>(null);

  const [ssoTarget, setSsoTarget] = useState<Collaborator | null>(null);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoSending, setSsoSending] = useState(false);
  const [ssoSuccess, setSsoSuccess] = useState(false);

  const handleOpenPayment = useCallback((collab: Collaborator) => {
    setPaymentTarget(collab);
  }, []);

  const handleClosePayment = useCallback(() => {
    setPaymentTarget(null);
  }, []);

  // A API desbloqueia o colaborador ao quitar o total em aberto; a linha
  // precisa refletir isso sem exigir recarregar a listagem inteira.
  const handlePaid = useCallback((result: CollaboratorPaymentResult) => {
    if (!result?.desbloqueado) return;

    setEntries((currentEntries) => currentEntries?.map((entry) => (
      entry.id_colaborador === result.id_colaborador
        ? { ...entry, bloqueio: false }
        : entry
    )));
  }, []);

  const handleOpenSso = useCallback((collab: Collaborator) => {
    setSsoTarget(collab);
    setSsoEmail(collab.email ?? '');
    setSsoSuccess(false);
  }, []);

  const handleCloseSso = useCallback(() => {
    if (ssoSending) return;
    setSsoTarget(null);
    setSsoEmail('');
    setSsoSuccess(false);
  }, [ssoSending]);

  const handleSendSsoLink = useCallback(async () => {
    if (!ssoTarget || !ssoEmail.trim()) return;
    try {
      setSsoSending(true);
      await sendCollaboratorSSOLink(companyId, ssoTarget.id_colaborador, ssoEmail.trim());
      setSsoSuccess(true);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Erro ao enviar link SSO.');
    } finally {
      setSsoSending(false);
    }
  }, [ssoTarget, ssoEmail, companyId]);

  const removeEntry = useCallback((entryId: Id) => {
    setEntries((currentEntries) => currentEntries?.filter((e) => e.id_colaborador !== entryId));
  }, []);

  useEffect(() => {
    if (hasCollaborators) {
      setEntries(collaborators);
    }
  }, [hasCollaborators, collaborators]);

  const handleOpenDialog = useCallback((collab: Collaborator) => {
    setSelected(collab);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (deleting) return;
    setDialogOpen(false);
    setSelected(null);
  }, [deleting]);

  const handleOpenProntuario = useCallback((collab: Collaborator) => {
    setProntuarioCollaborator(collab);
    setProntuarioOpen(true);
  }, []);

  const handleCloseProntuario = useCallback(() => {
    setProntuarioOpen(false);
    setProntuarioCollaborator(null);
  }, []);

  const handleSoftDelete = useCallback(
    async (targetCompanyId?: number) => {
      if (!selected) return;
      try {
        setDeleting(true);

        const destinationId = isAdmin ? Number(targetCompanyId) : FORCED_TARGET_ID;

        if (!Number.isInteger(destinationId) || destinationId <= 0) {
          alert("Selecione um ID de empresa de destino válido.");
          return;
        }
        if (Number(destinationId) === Number(companyId)) {
          alert("Não é possível mover para a mesma empresa.");
          return;
        }

        await softDeleteCollaborator(
          companyId,
          selected.id_colaborador,
          destinationId
        );
        removeEntry(selected.id_colaborador);
        await reloadList();
        handleCloseDialog();
      } catch (err: any) {
        alert(err?.message || "Erro ao realizar soft delete.");
      } finally {
        setDeleting(false);
      }
    },
    [isAdmin, FORCED_TARGET_ID, companyId, selected, reloadList, handleCloseDialog, removeEntry]
  );

  const handleHardDelete = useCallback(async () => {
    if (!selected) return;
    try {
      setDeleting(true);
      await deleteCollaboratorById(selected.id_empresa, selected.id_colaborador);
      removeEntry(selected.id_colaborador);
      await reloadList();
      handleCloseDialog();
    } catch (err: any) {
      alert(err?.message || "Erro ao apagar permanentemente.");
    } finally {
      setDeleting(false);
    }
  }, [selected, reloadList, handleCloseDialog, removeEntry]);

  return hasCollaborators ? (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Setor</strong></TableCell>
              <TableCell><strong>Cargo</strong></TableCell>
              <TableCell><strong>Unidade</strong></TableCell>
              <TableCell width={2}><strong>Bloqueado?</strong></TableCell>
              <TableCell width={2}><strong>Empresa bloqueada?</strong></TableCell>
              <TableCell><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries?.map((collaborator: Collaborator) => (
              <TableRow key={collaborator?.id_colaborador}>
                <TableCell>{collaborator?.id_colaborador}</TableCell>
                <TableCell>{collaborator?.nome}</TableCell>
                <TableCell>{collaborator?.setor}</TableCell>
                <TableCell>{collaborator?.cargo}</TableCell>
                <TableCell>{collaborator?.unidade}</TableCell>
                <TableCell width={2} sx={{ textAlign: "center" }}>
                  <IsBlockedCheckboxCell collaborator={collaborator} />
                </TableCell>
                <TableCell width={2} sx={{ textAlign: "center" }}>
                  <CompanyBlockCheckboxCell collaborator={collaborator} />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Ver prontuário">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenProntuario(collaborator)}
                        color={collaborator.prontuario ? "primary" : "default"}
                      >
                        <Assignment fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="Sessões em aberto / registrar pagamento">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenPayment(collaborator)}
                          color={collaborator?.bloqueio ? "error" : "default"}
                        >
                          <Paid fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Enviar link de cadastro SSO">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSso(collaborator)}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <EezycoinWalletDialog
                      ownerId={collaborator?.id_colaborador}
                      ownerType="COLABORATOR"
                      setError={(err: any) => {
                        if (err !== null) {
                          alert(err)
                        }
                      }}
                    />
                    <TableActions
                      editUrl={`/colaboradores/${collaborator?.id_colaborador}/editar`}
                      deleteCallback={async () => {
                        handleOpenDialog(collaborator);
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteCollaboratorDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        collaborator={selected}
        isAdmin={isAdmin}
        loading={deleting}
        onSoftDelete={handleSoftDelete}
        onHardDelete={isAdmin ? handleHardDelete : undefined}
      />

      <CollaboratorPaymentDialog
        open={Boolean(paymentTarget)}
        collaborator={paymentTarget}
        onClose={handleClosePayment}
        onPaid={handlePaid}
      />

      <ProntuarioModal
        open={prontuarioOpen}
        collaboratorName={prontuarioCollaborator?.nome ?? ""}
        prontuario={prontuarioCollaborator?.prontuario}
        onClose={handleCloseProntuario}
      />

      <Dialog open={Boolean(ssoTarget)} onClose={handleCloseSso} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar link de cadastro SSO</DialogTitle>
        <DialogContent>
          {ssoSuccess ? (
            <DialogContentText color="success.main">
              Link enviado com sucesso para <strong>{ssoEmail}</strong>.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText gutterBottom>
                Informe o e-mail de <strong>{ssoTarget?.nome}</strong> para enviar o link de
                cadastro vinculado à empresa.
              </DialogContentText>
              <TextField
                label="E-mail do colaborador"
                type="email"
                fullWidth
                size="small"
                value={ssoEmail}
                onChange={(e) => setSsoEmail(e.target.value)}
                disabled={ssoSending}
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseSso} disabled={ssoSending}>
            {ssoSuccess ? "Fechar" : "Cancelar"}
          </Button>
          {!ssoSuccess && (
            <Button
              variant="contained"
              onClick={handleSendSsoLink}
              disabled={ssoSending || !ssoEmail.trim()}
              startIcon={<LinkIcon />}
            >
              {ssoSending ? "Enviando…" : "Enviar link"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  ) : (
    <NoDataAlert />
  );
};

export default CollaboratorsTable;
